import { DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';
import { ValidateImportedItems } from '@retail/monitoring-assortment/components';
import {
  useMonitoringAssortmentActions,
  useValidationOnImportedItems,
} from '@retail/monitoring-assortment/context';
import { AddItemsTabs } from '@retail/my-assortment/components';
import { AddItemsTab, AssortmentItemMinimal } from '@retail/my-assortment/types';
import { UploadItemsContainer } from '@retail/products/components';
import { MgSupplierItemSearchContainer } from '@retail/products/containers';
import { useProductsTFunction } from '@retail/products/i18n';
import { MgSupplierItemSearchResult } from '@retail/products/types';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CentralAssortmentMemberView } from '@retail/my-assortment/containers';
import { useFilters } from '@retail/products/hooks';
import { searchFilters, SearchFilters } from '@retail/products/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedAssortmentId: number;
  assortmentId: number;
}

const csvData = 'MgItem-nummer;Finfo-nummer;Gtin-nummer';

export function AddItemsToMonitoringAssortment({
  open,
  onClose,
  selectedAssortmentId,
  assortmentId,
}: Props) {
  const t = useProductsTFunction();
  const [selectedTab, setSelectedTab] = useState<AddItemsTab>('ITEM_SEARCH');
  const [checkedItems, setCheckedItems] = useState<MgSupplierItemSearchResult[]>([]);
  const [checkedAssortmentItems, setCheckedAssortmentItems] = useState<AssortmentItemMinimal[]>([]);

  const { addItemsToAssortment } = useMonitoringAssortmentActions();
  const { importedItems, validationState, resetImportedAssortmentStates, loadingValidateItems } =
    useValidationOnImportedItems();

  const { resetFilters } = useFilters<SearchFilters>(searchFilters);

  const itemsToAdd = useMemo(
    () =>
      selectedTab === 'ITEM_SEARCH'
        ? checkedItems.map((item) => item.mgItemNumber)
        : selectedTab === 'CENTRAL_ASSORTMENTS'
        ? checkedAssortmentItems.map((item) => item.mgItemNumber)
        : selectedTab === 'IMPORT'
        ? (importedItems.map(({ mgItemNumber }) => mgItemNumber) as number[])
        : [],
    [checkedAssortmentItems, checkedItems, importedItems, selectedTab]
  );

  const handleAddItemsToAssorment = useCallback(() => {
    addItemsToAssortment({
      mgItemNumbers: itemsToAdd,
      finfoNumbers:
        selectedTab === 'IMPORT'
          ? (importedItems.map(({ finfoNumber }) => finfoNumber) as number[])
          : [],
      assortmentId: selectedAssortmentId,
    }).then(() => {
      onClose();
      resetFilters();
      resetImportedAssortmentStates();
    });
  }, [
    addItemsToAssortment,
    importedItems,
    itemsToAdd,
    onClose,
    resetFilters,
    resetImportedAssortmentStates,
    selectedAssortmentId,
    selectedTab,
  ]);

  useEffect(() => {
    if (selectedTab !== 'IMPORT') resetImportedAssortmentStates();
    if (selectedTab !== 'ITEM_SEARCH') {
      setCheckedItems([]);
      resetFilters();
    }
    if (selectedTab !== 'CENTRAL_ASSORTMENTS') setCheckedAssortmentItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        resetFilters();
      }}
      fullScreen
      sx={{ padding: 4, '.MuiPaper-root': { gap: 0 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <AddItemsTabs
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          includeCentralAssortments={true}
        />
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexGrow: 1 }} className="padding-0">
        {selectedTab === 'ITEM_SEARCH' && (
          <SuspenseWithSentryErrorBoundary>
            <MgSupplierItemSearchContainer
              checkedItems={checkedItems}
              setCheckedItems={setCheckedItems}
              assortmentId={assortmentId}
            />
          </SuspenseWithSentryErrorBoundary>
        )}
        {selectedTab === 'IMPORT' && (
          <Stack display="flex" flexGrow={1} justifyContent="center">
            <UploadItemsContainer
              isLarge
              loadingValidateItems={loadingValidateItems}
              csvData={csvData}
              resetImportedAssortmentStates={resetImportedAssortmentStates}
              renderValidation={
                <ValidateImportedItems
                  importedItems={importedItems}
                  invalidItemCodes={validationState}
                />
              }
            />
          </Stack>
        )}
        {selectedTab === 'CENTRAL_ASSORTMENTS' && (
          <SuspenseWithSentryErrorBoundary>
            <CentralAssortmentMemberView
              includeTitle={false}
              hideActionToolbar
              itemsChecked={checkedAssortmentItems}
              setItemsChecked={setCheckedAssortmentItems}
            />
          </SuspenseWithSentryErrorBoundary>
        )}
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={2}>
          <TextButton onClick={onClose}>{t('products.cancel')}</TextButton>
          <ContainedButton color="secondary" size="small" onClick={handleAddItemsToAssorment}>
            {t('products.mgItem.add')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
